import { NewNotificationIcon } from '@/Components/Common/Icons';
import useGetUserEvents from '@/Components/Hooks/useGetUserEvents';
import { roundUpToNextHour } from '@/Components/Sensors/SensorUtils';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import '../../Dashboard.less';
import { DashboardSSEEventContext } from '../../DashboardContext';
import { useRegisterReportSection } from '../../reportUtils';
import { getSupportedAlertEventTypesForCategory } from '../../utils';
import AlertsCard from './AlertsCard';
import {
  getTopApiAlerts,
  isSupportedDashboardAlertType,
  mergeAlertCards,
  normalizeSseAlertForCard,
} from './alertCardsUtils';

const getInitialDates = () => {
  const now = new Date();
  const windowEnd = roundUpToNextHour(now);
  const days = 2;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const windowStart = new Date(windowEnd.getTime() - days * MS_PER_DAY);
  return { start: windowStart.toISOString(), end: windowEnd.toISOString() };
};

const Alerts = ({
  timezone,
  is24HourFormat,
  useruuid,
  locale,
  hc200DeviceUuid,
  isAlertsApplicable,
  redirectToAlertsTab,
  supportedCategories,
}) => {
  const { start: startDate, end: endDate } = getInitialDates();
  const trans = useSelector(translator);
  const supportedAlertEventTypes = useMemo(
    () => getSupportedAlertEventTypesForCategory(supportedCategories),
    [supportedCategories]
  );

  const [cardAlerts, setCardAlerts] = useState([]);

  const { data: alerts } = useGetUserEvents({
    useruuid,
    deviceuuid: hc200DeviceUuid,
    startDate,
    endDate,
    eventTypes: supportedAlertEventTypes,
    open,
  });

  useEffect(() => setCardAlerts([]), [useruuid]);

  useEffect(() => {
    const apiTopAlerts = getTopApiAlerts(alerts ?? []);

    setCardAlerts((previousAlerts) =>
      isEmpty(previousAlerts) ? apiTopAlerts : mergeAlertCards(previousAlerts, apiTopAlerts)
    );
  }, [alerts]);

  const { latestEvent: sseEvent } = useContext(DashboardSSEEventContext);

  useEffect(() => {
    const normalizedSseAlert = normalizeSseAlertForCard(sseEvent);

    if (
      isEmpty(normalizedSseAlert) ||
      !isSupportedDashboardAlertType(get(normalizedSseAlert, 'message_type', ''))
    ) {
      return;
    }

    setCardAlerts((previousAlerts) => mergeAlertCards([normalizedSseAlert], previousAlerts));
  }, [sseEvent]);

  // Register the alerts actually shown on the dashboard so the PDF report never
  // loses information (e.g. real-time SSE alerts absent from the API history).
  useRegisterReportSection('alerts', cardAlerts, [cardAlerts]);

  const shouldShowSkeleton = Boolean(isAlertsApplicable && isEmpty(cardAlerts));
  if (isEmpty(cardAlerts) && !isAlertsApplicable) return null;

  return (
    <div className="alert-section">
      <div className="section-header">
        <NewNotificationIcon className="section-icon" />
        <h3 className="section-title">{trans('ALERTS')}</h3>
      </div>
      <AlertsCard
        alerts={cardAlerts}
        is24hours={is24HourFormat}
        timezone={timezone}
        locale={locale}
        isFetchingAlerts={shouldShowSkeleton}
      />
      {cardAlerts.length > 0 && (
        <div className="more-alerts">
          <button className="show-more-alerts" onClick={redirectToAlertsTab}>
            {trans('SHOW_MORE_ALERTS')}
          </button>
        </div>
      )}
    </div>
  );
};

Alerts.propTypes = {
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  useruuid: PropTypes.string,
  locale: PropTypes.string,
  hc200DeviceUuid: PropTypes.string,
  isAlertsApplicable: PropTypes.bool,
  redirectToAlertsTab: PropTypes.func,
  supportedCategories: PropTypes.array,
};

export default Alerts;
